import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import Button from '@whitelabel/component-library/src/components/Button';
import {
  StyledNav,
  StyledNavBarToggler,
  StyledNavButtonGroups,
  StyledContainer,
  StyledBrandContainer,
} from '@whitelabel/rentalcover-shared/styled/StyledPrimaryNavBar';
import { SITE_NAME } from '@whitelabel/rentalcover-shared/helpers/constants';
import Brand from '@whitelabel/rentalcover-shared/components/Brand';
import { Media } from '@whitelabel/helpers/media';
import Loading from '@whitelabel/rentalcover-shared/components/Loading';
import rentalcoverSharedMessages from '@whitelabel/rentalcover-shared/helpers/messages/commonMsg';
import rentalcoverLogoDark from '@whitelabel/rentalcover-shared/media/images/rentalcover-logo-dark.svg';
import { HelpButton } from '@whitelabel/rentalcover-shared/components/NavButtons';
import CustomerDropdown from '@whitelabel/rentalcover-shared/components/CustomerDropdown';
import { LocaleButtonDesktop } from '@whitelabel/rentalcover-shared/components/LocaleButton/LocaleButtonDesktop';

import { useAppSelector } from '../../../../store';
import { selectCurrentCustomer } from '../../../../store/slices/customer';
import LocaleModal from '../../../../components/LocaleModal';
import { useRCOffers } from '../../../../helpers/hooks';

const brand = {
  title: SITE_NAME,
  logo: rentalcoverLogoDark,
};

interface IPrimaryNavBarProps {
  showHelpButton: boolean;
  showGetQuoteButton?: boolean;
  showCustomer?: boolean;
  isMobileNavOpen: boolean;
  toggleNav: () => void;
}

const PrimaryNavBar = ({
  showHelpButton,
  showGetQuoteButton,
  showCustomer,
  isMobileNavOpen,
  toggleNav,
}: IPrimaryNavBarProps): JSX.Element => {
  const { locale, formatMessage } = useIntl();
  const { pathname } = useLocation();
  const rentalcoverLogoHref = pathname.includes('/account/profile') ? `/${locale}/account` : `/${locale}/`;
  const customer = useAppSelector(selectCurrentCustomer);
  const [isLocaleModalOpen, setIsLocaleModalOpen] = useState(false);
  const { showOffers } = useRCOffers();

  const toggleLocaleModal = () => {
    setIsLocaleModalOpen(!isLocaleModalOpen);
  };

  const renderCustomerButton = () =>
    customer ? <CustomerDropdown customer={customer} showOffers={showOffers} /> : <Loading $height="1.5rem" />;

  return (
    <StyledNav id="NavBar">
      <StyledContainer>
        <StyledBrandContainer>
          <a href={rentalcoverLogoHref}>
            <Brand {...brand} />
          </a>
        </StyledBrandContainer>
        <div>
          <Media lessThan="lg">
            <StyledNavBarToggler
              tag={Button}
              buttonType="tertiary"
              $active={isMobileNavOpen}
              onClick={toggleNav}
              data-test-id="NavBar-toggler"
            />
          </Media>
          <Media greaterThanOrEqual="lg">
            <StyledNavButtonGroups>
              {showHelpButton && <HelpButton />}
              <LocaleButtonDesktop toggle={toggleLocaleModal} />
              {showCustomer && renderCustomerButton()}
              {showGetQuoteButton && (
                <Button tag="a" href={`/${locale}/`} className="get-quote" buttonType="primary">
                  {formatMessage(rentalcoverSharedMessages.getQuoteButton)}
                </Button>
              )}
            </StyledNavButtonGroups>
          </Media>
        </div>
      </StyledContainer>
      <LocaleModal isOpen={isLocaleModalOpen} toggle={toggleLocaleModal} />
    </StyledNav>
  );
};

export default PrimaryNavBar;
